import { BASELINE_FITNESS, WW_PRICING_C_FITNESS_PLANS } from './SubscriptionPlans';

type PlanPrice = {
  price: number;
  discPrice: number;
  renewalPrice: number;
};

type FourWeekLinePlanIds = {
  trial: string;
  fourWeeks: string;
  fourWeeksDisc: string;
  twelveWeeks: string;
};

type FourWeekLinePricing = {
  ids: FourWeekLinePlanIds;
  trial: Omit<PlanPrice, 'discPrice'> & { crossedPrice: number };
  fourWeeks: PlanPrice;
  twelveWeeks: Omit<PlanPrice, 'discPrice'>;
  twelveWeeksDisc?: { chargebeeId: string; price: number };
  fitness: AddonPaymentModel[];
};

type WwPlanIds = {
  trial: string;
  month: string;
  monthDisc: string;
  threeMonths: string;
};

type WwPricing = {
  ids: WwPlanIds;
  trialRenewalPrice: number;
  month: PlanPrice;
  threeMonths: Omit<PlanPrice, 'discPrice'>;
};

const FITNESS_PLANS: AddonPaymentModel[] = [
  {
    id: 1,
    fullPrice: 29.99,
    periodKey: 'subscription_period_4week',
    planNameUpper: '',
    currencyCode: 'USD',
    currencySymbol: '$',
    standard: {
      chargebeeId: 'Fitness-e2-USD-Every-4-weeks',
      discPrice: 19.99,
      roundedDiscPrice: 1999,
    },
    super: {
      chargebeeId: 'Fitness-e2d-USD-Every-4-weeks',
      discPrice: 9.99,
      roundedDiscPrice: 999,
    },
  },
  {
    id: 2,
    fullPrice: 59.99,
    periodKey: 'subscription_period_12week',
    planNameUpper: '',
    currencyCode: 'USD',
    currencySymbol: '$',
    standard: {
      chargebeeId: 'Fitness-e2-USD-Every-12-weeks',
      discPrice: 39.99,
      roundedDiscPrice: 3999,
    },
    super: {
      chargebeeId: 'Fitness-e2d-USD-Every-12-weeks',
      discPrice: 19.99,
      roundedDiscPrice: 1999,
    },
  },
];

const toCents = (price: number): number => Math.round(price * 100);
const perDay = (price: number, days: number): number => Math.round((price / days) * 100) / 100;
const perDayRoundedDown = (price: number, days: number): number => Math.floor((price / days) * 100) / 100;

function createFourWeekLinePricingPlans({ ids, trial, fourWeeks, twelveWeeks, twelveWeeksDisc, fitness }: FourWeekLinePricing): SubscriptionModel[] {
  const trialOffer = {
    discPrice: trial.price,
    roundedDiscPrice: toCents(trial.price),
    discPricePerDay: perDayRoundedDown(trial.price, 7),
    chargebeeId: ids.trial,
  };
  const twelveWeeksOffer = {
    discPrice: twelveWeeks.price,
    roundedDiscPrice: toCents(twelveWeeks.price),
    discPricePerDay: perDayRoundedDown(twelveWeeks.price, 84),
    chargebeeId: ids.twelveWeeks,
  };
  const twelveWeeksDiscOffer = twelveWeeksDisc
    ? {
      discPrice: twelveWeeksDisc.price,
      roundedDiscPrice: toCents(twelveWeeksDisc.price),
      discPricePerDay: perDayRoundedDown(twelveWeeksDisc.price, 84),
      chargebeeId: twelveWeeksDisc.chargebeeId,
    }
    : twelveWeeksOffer;

  return [
    {
      id: 1,
      fullPrice: trial.crossedPrice,
      fullPricePerDay: perDayRoundedDown(trial.crossedPrice, 7),
      planName: 'subscription_1week_kegel_plan',
      planNameUpper: 'lp_v7_plan_1week',
      periodKey: 'subscription_period_1week',
      currencyCode: 'USD',
      currencySymbol: '$',
      planCode: '1 week',
      weeklyRenewalPrice: trial.renewalPrice,
      renewalPeriodKey: 'subscription_period_4week',
      standard: trialOffer,
      super: trialOffer,
      fitness: fitness[0],
    },
    {
      id: 2,
      fullPrice: fourWeeks.renewalPrice,
      fullPricePerDay: perDayRoundedDown(fourWeeks.renewalPrice, 28),
      planName: 'subscription_4week_kegel_plan',
      planNameUpper: 'lp_v7_plan_4week',
      periodKey: 'subscription_period_4week',
      currencyCode: 'USD',
      currencySymbol: '$',
      planCode: '4 week',
      standard: {
        discPrice: fourWeeks.price,
        roundedDiscPrice: toCents(fourWeeks.price),
        discPricePerDay: perDayRoundedDown(fourWeeks.price, 28),
        chargebeeId: ids.fourWeeks,
      },
      super: {
        discPrice: fourWeeks.discPrice,
        roundedDiscPrice: toCents(fourWeeks.discPrice),
        discPricePerDay: perDayRoundedDown(fourWeeks.discPrice, 28),
        chargebeeId: ids.fourWeeksDisc,
      },
      fitness: fitness[0],
    },
    {
      id: 3,
      fullPrice: twelveWeeks.renewalPrice,
      fullPricePerDay: perDayRoundedDown(twelveWeeks.renewalPrice, 84),
      planName: 'subscription_12week_kegel_plan',
      planNameUpper: 'lp_v7_plan_12week',
      periodKey: 'subscription_period_12week',
      currencyCode: 'USD',
      currencySymbol: '$',
      planCode: '12 week',
      standard: twelveWeeksOffer,
      super: twelveWeeksDiscOffer,
      fitness: fitness[1],
    },
  ];
}

export const PRICING_11_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3499-w1w4-usd',
    fourWeeks: 'kegel-pw2-1999-3499-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-3499-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 34.99 },
  fourWeeks: { price: 19.99, discPrice: 14.99, renewalPrice: 34.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: FITNESS_PLANS,
});

export const PRICING_12_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3999-w1w4-usd',
    fourWeeks: 'kegel-pw2-1999-3999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-3999-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 19.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: FITNESS_PLANS,
});

export const PRICING_13_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3999-w1w4-usd',
    fourWeeks: 'kegel-pw2-2299-3999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-3999-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 22.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: FITNESS_PLANS,
});

export const PRICING_14_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3999-w1w4-usd',
    fourWeeks: 'kegel-pw2-2499-3999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-3999-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 24.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: FITNESS_PLANS,
});

export const PRICING_15_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-1399-5999-w1w4-usd',
    fourWeeks: 'kegel-pw2-2999-5999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-5999-w4-usd',
    twelveWeeks: 'kegel-pw2-4999-9999-w12-usd',
  },
  trial: { price: 13.99, crossedPrice: 21.69, renewalPrice: 59.99 },
  fourWeeks: { price: 29.99, discPrice: 14.99, renewalPrice: 59.99 },
  twelveWeeks: { price: 49.99, renewalPrice: 99.99 },
  fitness: FITNESS_PLANS,
});

export const PRICING_21_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3499-w1w4-usd',
    fourWeeks: 'kegel-pw2-1999-3499-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-3499-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 34.99 },
  fourWeeks: { price: 19.99, discPrice: 14.99, renewalPrice: 34.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: WW_PRICING_C_FITNESS_PLANS,
});

export const PRICING_22_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3999-w1w4-usd',
    fourWeeks: 'kegel-pw2-1999-3999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-3999-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 19.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: WW_PRICING_C_FITNESS_PLANS,
});

export const PRICING_23_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3999-w1w4-usd',
    fourWeeks: 'kegel-pw2-2499-3999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-3999-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 24.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: WW_PRICING_C_FITNESS_PLANS,
});

export const PRICING_24_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-999-3999-w1w4-usd',
    fourWeeks: 'kegel-pw2-999-3999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-699-3999-w4-usd',
    twelveWeeks: 'kegel-pw2-3999-6799-w12-usd',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 9.99, discPrice: 6.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  fitness: WW_PRICING_C_FITNESS_PLANS,
});

export const PRICING_25_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pw2-1399-5999-w1w4-usd',
    fourWeeks: 'kegel-pw2-2999-5999-w4-usd',
    fourWeeksDisc: 'kegel-pw2-disc-1499-5999-w4-usd',
    twelveWeeks: 'kegel-pw2-4999-9999-w12-usd',
  },
  trial: { price: 13.99, crossedPrice: 21.69, renewalPrice: 59.99 },
  fourWeeks: { price: 29.99, discPrice: 14.99, renewalPrice: 59.99 },
  twelveWeeks: { price: 49.99, renewalPrice: 99.99 },
  fitness: WW_PRICING_C_FITNESS_PLANS,
});

export const US_PRICING_TEST_2_A_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pricingtest2-aw-USD-Every-4-weeks',
    fourWeeks: 'kegel-pricingtest2-a-USD-Every-4-weeks',
    fourWeeksDisc: 'kegel-pricingtest2-a-d-USD-Every-4-weeks',
    twelveWeeks: 'kegel-pricingtest2-a-USD-Every-12-weeks',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 34.99 },
  fourWeeks: { price: 19.99, discPrice: 14.99, renewalPrice: 34.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  twelveWeeksDisc: { chargebeeId: 'kegel-pricingtest2-a-d-USD-Every-12-weeks', price: 24.99 },
  fitness: FITNESS_PLANS,
});

export const US_PRICING_TEST_2_B_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pricingtest2-bw-USD-Every-4-weeks',
    fourWeeks: 'kegel-pricingtest2-b-USD-Every-4-weeks',
    fourWeeksDisc: 'kegel-pricingtest2-b-d-USD-Every-4-weeks',
    twelveWeeks: 'kegel-pricingtest2-a-USD-Every-12-weeks',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 19.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  twelveWeeksDisc: { chargebeeId: 'kegel-pricingtest2-a-d-USD-Every-12-weeks', price: 24.99 },
  fitness: FITNESS_PLANS,
});

export const US_PRICING_TEST_2_C_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pricingtest2-bw-USD-Every-4-weeks',
    fourWeeks: 'kegel-pricingtest2-c-USD-Every-4-weeks',
    fourWeeksDisc: 'kegel-pricingtest2-b-d-USD-Every-4-weeks',
    twelveWeeks: 'kegel-pricingtest2-a-USD-Every-12-weeks',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 22.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  twelveWeeksDisc: { chargebeeId: 'kegel-pricingtest2-a-d-USD-Every-12-weeks', price: 24.99 },
  fitness: FITNESS_PLANS,
});

export const US_PRICING_TEST_2_D_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pricingtest2-bw-USD-Every-4-weeks',
    fourWeeks: 'kegel-pricingtest2-d-USD-Every-4-weeks',
    fourWeeksDisc: 'kegel-pricingtest2-b-d-USD-Every-4-weeks',
    twelveWeeks: 'kegel-pricingtest2-a-USD-Every-12-weeks',
  },
  trial: { price: 9.99, crossedPrice: 15.49, renewalPrice: 39.99 },
  fourWeeks: { price: 24.99, discPrice: 14.99, renewalPrice: 39.99 },
  twelveWeeks: { price: 39.99, renewalPrice: 67.99 },
  twelveWeeksDisc: { chargebeeId: 'kegel-pricingtest2-a-d-USD-Every-12-weeks', price: 24.99 },
  fitness: FITNESS_PLANS,
});

export const US_PRICING_TEST_2_E_PLANS = createFourWeekLinePricingPlans({
  ids: {
    trial: 'kegel-pricingtest2-ew-USD-Every-4-weeks',
    fourWeeks: 'kegel-pricingtest2-e-USD-Every-4-weeks',
    fourWeeksDisc: 'kegel-pricingtest2-e-d-USD-Every-4-weeks',
    twelveWeeks: 'kegel-pricingtest2-e-USD-Every-12-weeks',
  },
  trial: { price: 13.99, crossedPrice: 21.69, renewalPrice: 59.99 },
  fourWeeks: { price: 29.99, discPrice: 14.99, renewalPrice: 59.99 },
  twelveWeeks: { price: 49.99, renewalPrice: 99.99 },
  twelveWeeksDisc: { chargebeeId: 'kegel-pricingtest2-e-d-USD-Every-12-weeks', price: 24.99 },
  fitness: FITNESS_PLANS,
});

const WW_TRIAL = {
  fullPrice: 10.59,
  fullPricePerDay: 1.5,
  discPrice: 6.99,
  discPricePerDay: 0.99,
};

const [WW_TRIAL_FITNESS, WW_MONTH_FITNESS, WW_THREE_MONTHS_FITNESS] = BASELINE_FITNESS;

function createWwPricingPlans({ ids, trialRenewalPrice, month, threeMonths }: WwPricing): SubscriptionModel[] {
  const trialOffer = {
    discPrice: WW_TRIAL.discPrice,
    roundedDiscPrice: toCents(WW_TRIAL.discPrice),
    discPricePerDay: WW_TRIAL.discPricePerDay,
    chargebeeId: ids.trial,
  };
  const threeMonthsOffer = {
    discPrice: threeMonths.price,
    roundedDiscPrice: toCents(threeMonths.price),
    discPricePerDay: perDay(threeMonths.price, 90),
    chargebeeId: ids.threeMonths,
  };

  return [
    {
      id: 1,
      fullPrice: WW_TRIAL.fullPrice,
      fullPricePerDay: WW_TRIAL.fullPricePerDay,
      planName: 'subscription_1week_kegel_plan',
      planNameUpper: 'lp_v7_plan_1week',
      periodKey: 'subscription_period_1week',
      renewalPeriodKey: 'subscription_period_1month',
      currencyCode: 'USD',
      currencySymbol: '$',
      planCode: '1 week',
      weeklyRenewalPrice: trialRenewalPrice,
      standard: trialOffer,
      super: trialOffer,
      fitness: WW_TRIAL_FITNESS,
    },
    {
      id: 2,
      fullPrice: month.renewalPrice,
      fullPricePerDay: perDay(month.renewalPrice, 31),
      planName: 'subscription_1month_kegel_plan',
      planNameUpper: 'lp_v7_plan_1month',
      periodKey: 'subscription_period_1month',
      currencyCode: 'USD',
      currencySymbol: '$',
      planCode: '1 month',
      standard: {
        discPrice: month.price,
        roundedDiscPrice: toCents(month.price),
        discPricePerDay: perDay(month.price, 31),
        chargebeeId: ids.month,
      },
      super: {
        discPrice: month.discPrice,
        roundedDiscPrice: toCents(month.discPrice),
        discPricePerDay: perDay(month.discPrice, 31),
        chargebeeId: ids.monthDisc,
      },
      fitness: WW_MONTH_FITNESS,
    },
    {
      id: 3,
      fullPrice: threeMonths.renewalPrice,
      fullPricePerDay: perDay(threeMonths.renewalPrice, 90),
      planName: 'subscription_3months_kegel_plan',
      planNameUpper: 'lp_v7_plan_3months',
      periodKey: 'subscription_period_3months',
      currencyCode: 'USD',
      currencySymbol: '$',
      planCode: '3 month',
      standard: threeMonthsOffer,
      super: threeMonthsOffer,
      fitness: WW_THREE_MONTHS_FITNESS,
    },
  ];
}

export const PRICING_31_PLANS = createWwPricingPlans({
  ids: {
    trial: 'kegel-pw2-699-3499-w1m1-usd',
    month: 'kegel-pw2-1519-3499-m1-usd',
    monthDisc: 'kegel-pw2-disc-1199-3499-m1-usd',
    threeMonths: 'kegel-pw2-2599-6799-m3-usd',
  },
  trialRenewalPrice: 34.99,
  month: { price: 15.19, discPrice: 11.99, renewalPrice: 34.99 },
  threeMonths: { price: 25.99, renewalPrice: 67.99 },
});

export const PRICING_32_PLANS = createWwPricingPlans({
  ids: {
    trial: 'kegel-pw2-699-3999-w1m1-usd',
    month: 'kegel-pw2-1519-3999-m1-usd',
    monthDisc: 'kegel-pw2-disc-1199-3999-m1-usd',
    threeMonths: 'kegel-pw2-2599-6799-m3-usd',
  },
  trialRenewalPrice: 39.99,
  month: { price: 15.19, discPrice: 11.99, renewalPrice: 39.99 },
  threeMonths: { price: 25.99, renewalPrice: 67.99 },
});

export const PRICING_33_PLANS = createWwPricingPlans({
  ids: {
    trial: 'kegel-l1nw-USD-Monthly',
    month: 'kegel-l1nd-USD-Monthly',
    monthDisc: 'kegel-pw2-disc-799-3100-m1-usd',
    threeMonths: 'kegel-l1n-USD-Every-3-months',
  },
  trialRenewalPrice: 31,
  month: { price: 11.99, discPrice: 7.99, renewalPrice: 31 },
  threeMonths: { price: 25.99, renewalPrice: 53.04 },
});

export const PRICING_34_PLANS = createWwPricingPlans({
  ids: {
    trial: 'kegel-pw2-699-1999-w1m1-usd',
    month: 'kegel-pw2-999-1999-m1-usd',
    monthDisc: 'kegel-pw2-disc-799-1999-m1-usd',
    threeMonths: 'kegel-l1n-USD-Every-3-months',
  },
  trialRenewalPrice: 19.99,
  month: { price: 9.99, discPrice: 7.99, renewalPrice: 19.99 },
  threeMonths: { price: 25.99, renewalPrice: 53.04 },
});

export const PRICING_35_PLANS = createWwPricingPlans({
  ids: {
    trial: 'kegel-l1nw-USD-Monthly',
    month: 'kegel-pw2-999-3100-m1-usd',
    monthDisc: 'kegel-pw2-disc-799-3100-m1-usd',
    threeMonths: 'kegel-l1n-USD-Every-3-months',
  },
  trialRenewalPrice: 31,
  month: { price: 9.99, discPrice: 7.99, renewalPrice: 31 },
  threeMonths: { price: 25.99, renewalPrice: 53.04 },
});
